import React, { useEffect, useState } from "react";
import { ModalX, PageHeader } from "../../components";
import { Alert, Button, Checkbox, Chip, IconButton, Input, Option, Radio, Select, Spinner, Tab, TabPanel, Tabs, TabsBody, TabsHeader, Typography } from "@material-tailwind/react";
import { AccessibilityIcon, ArrowDownIcon, ArrowUpIcon, BookTextIcon, Edit2Icon, EyeIcon, ListIcon, ShapesIcon, UserIcon } from "lucide-react";
import { XIcon } from "lucide-react";
import moment from "moment";
import { convertToPresentableText, getOrdinal, NotifyError } from "../../utils/functions";
import { ACCOMMODATION_TYPES_ENUM, DESK_TYPES_ENUM, OPERATIONAL_STATUSES, OPERATIONAL_STATUSES_ENUM } from "../../utils/enums";
import { Link } from "react-router-dom";
import { useAddSeat, useGetSeats, useUpdateSeat } from "../../custom-hooks/use-seats";
import { useGetCoworkingSpaces } from "../../custom-hooks/use-coworking-spaces";
import { useConfirm } from "../../custom-hooks/confirm-dialog";
import { useDeleteBooking } from "../../custom-hooks/use-bookings";
import { InterpretOperationalStatus } from "../../utils/ui-interpreters";
import { usePermissions } from "../../custom-hooks/use-permissions";

const INITIAL_FORM_DATA = {
  // Basic Information
  seatCode: '',
  coworking: '',
  // Physical Attributes
  seatType: '',
  // position: {
  //   coordinates: {
  //     x: { type: Number }, // Position on floor plan
  //     y: { type: Number }
  //   },
  //   zone: { type: String }, // e.g., 'window', 'interior', 'corner'
  //   placement: { type: String, enum: ['north', 'south', 'east', 'west'] }
  // },
  // Seat Features
  hasMonitorArm: false,
  hasKeyboard: false,
  hasMouse: false,
  hasErgonomicChair: false,
  hasFootRest: false,
  hasPrivacyScreen: false,
  hasUSBPorts: false,
  powerOutlets: 2,
  hasWiredInternet: true,
  dedicatedInternet: false,
  dedicatedClimateControl: false,
  internetSpeed: 250, // in Mbps
  deskType: '',
  // Equipment and Assets
  // equipment: [{
  //     item: { type: String, enum: ['monitor', 'docking_station', 'phone', 'desk_lamp', 'webcam'] },
  //     serialNumber: { type: String },
  //     model: { type: String },
  //     lastMaintenanceDate: { type: Date }
  // }],
  // Accessibility Features
  isWheelchairAccessible: false,
  hasAdjustableHeight: false,
  hasSpecialLighting: false,
  requiresSpecialAccommodation: false,
  accommodationType: [],
  // Status and Availability
  status: 'available',
  isActive: true,
  needsMaintenance: false,
  underMaintenance: false,
  lastMaintenanceDate: '',
  nextMaintenanceDate: '',
  maintenanceNotes: '',
  // Usage and Assignment
  currentAssignmentType: '',
  currentAssignmentUser: '',
  currentAssignmentStartDate: '',
  currentAssignmentEndDate: '',
  // Booking Rules
  isBookable: true,
  naturalLight: false,
  noiseLevel: 'medium',
  nearWindow: false,
  ventilation: '',
  temperature: '',
}

const addModalTabs = [
  {
    label: 'General',
    value: 'general',
    icon: <BookTextIcon size={12} />,
  },
  {
    label: 'Features',
    value: 'features',
    icon: <ListIcon size={12} />,
  },
  {
    label: 'Equipment',
    value: 'equipment',
    icon: <ShapesIcon size={12} />,
  },
  {
    label: 'Accessibility',
    value: 'accessiblity',
    icon: <AccessibilityIcon size={12} />,
  },
  // {
  //   label: 'Booking',
  //   value: 'booking',
  //   icon: <BookOpenCheckIcon size={12} />,
  // },
]
const SeatPage = () => {

  // State variables
  // const [branches, setBranches] = useState([]);
  // const [buildings, setBuildings] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedSeatId, setSelectedSeatId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [filterStatus, setFilterStatus] = useState(OPERATIONAL_STATUSES.OPERATIONAL);
  const [filterSeatType, setFilterSeatType] = useState('');
  const [sort, setSort] = useState('-createdDate');
  // const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
  // const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);
  useEffect(() => {
    setIsTyping(true);
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setIsTyping(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const addSeatMutation = useAddSeat();
  const updateSeatMutation = useUpdateSeat();

  const { data: seats, isLoading: isSeatsLoading, isError: isSeatsError } = useGetSeats('all', {
    // _page: page,
    // _limit: limit,
    _search: debouncedSearch,
    _sort: sort,
    seatType: filterSeatType,
    status: filterStatus,
    // ...filters
  });
  const { data: spaces, isLoading: isSpacesLoading, isError: isSpacesError } = useGetCoworkingSpaces();
  const { can } = usePermissions();

  const { confirm, ConfirmationDialog } = useConfirm();
  const deleteBookingMutation = useDeleteBooking();

  // Handle form input change
  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBoxChange = async (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };


  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (isUpdating && formData.status === OPERATIONAL_STATUSES.CLOSED && formData.bookings && formData.bookings.length > 0) {
      const isConfirmed = await confirm({
        title: "Cancel Active Bookings",
        message: `This seat has ${formData.bookings.length} active booking(s). Closing it will cancel the booking(s). Do you want to proceed?`,
        confirmText: "Yes, Cancel Bookings",
        confirmBtnColor: "red"
      });
      if (isConfirmed) {
        try {
          for (const booking of formData.bookings) {
            await deleteBookingMutation.mutateAsync(booking._id);
          }
        } catch (error) {
          NotifyError("Failed to cancel bookings", error);
          return;
        }
      } else {
        return;
      }
    }

    try {
      if (isUpdating) {
        const updatedSeat = {
          seatCode: formData.seatCode,
          seatType: formData.seatType,
          coworking: formData.coworking,
          hasMonitorArm: formData.hasMonitorArm,
          hasKeyboard: formData.hasKeyboard,
          hasMouse: formData.hasMouse,
          hasErgonomicChair: formData.hasErgonomicChair,
          hasFootRest: formData.hasFootRest,
          hasPrivacyScreen: formData.hasPrivacyScreen,
          hasUSBPorts: formData.hasUSBPorts,
          powerOutlets: formData.powerOutlets,
          hasWiredInternet: formData.hasWiredInternet,
          dedicatedInternet: formData.dedicatedInternet,
          dedicatedClimateControl: formData.dedicatedClimateControl,
          internetSpeed: formData.internetSpeed,
          deskType: formData.deskType,
          isWheelchairAccessible: formData.isWheelchairAccessible,
          hasAdjustableHeight: formData.hasAdjustableHeight,
          hasSpecialLighting: formData.hasSpecialLighting,
          requiresSpecialAccommodation: formData.requiresSpecialAccommodation,
          accommodationType: formData.accommodationType,
          status: formData.status,
          isActive: formData.isActive,
          needsMaintenance: formData.needsMaintenance,
          underMaintenance: formData.underMaintenance,
          lastMaintenanceDate: formData.lastMaintenanceDate,
          nextMaintenanceDate: formData.nextMaintenanceDate,
          maintenanceNotes: formData.maintenanceNotes,
          // currentAssignmentType: formData.currentAssignmentType,
          // currentAssignmentUser: formData.currentAssignmentUser,
          // currentAssignmentStartDate: formData.currentAssignmentStartDate,
          // currentAssignmentEndDate: formData.currentAssignmentEndDate,
          isBookable: formData.isBookable,
          naturalLight: formData.naturalLight,
          noiseLevel: formData.noiseLevel,
          nearWindow: formData.nearWindow,
          ventilation: formData.ventilation,
          temperature: formData.temperature,
        };
        await updateSeatMutation.mutateAsync({ seatId: selectedSeatId, data: updatedSeat });
      } else {
        // Add new building logic
        const newSeat = {
          seatCode: formData.seatCode,
          seatType: formData.seatType,
          hasMonitorArm: formData.hasMonitorArm,
          hasKeyboard: formData.hasKeyboard,
          hasMouse: formData.hasMouse,
          hasErgonomicChair: formData.hasErgonomicChair,
          hasFootRest: formData.hasFootRest,
          hasPrivacyScreen: formData.hasPrivacyScreen,
          hasUSBPorts: formData.hasUSBPorts,
          powerOutlets: formData.powerOutlets,
          hasWiredInternet: formData.hasWiredInternet,
          dedicatedInternet: formData.dedicatedInternet,
          dedicatedClimateControl: formData.dedicatedClimateControl,
          internetSpeed: formData.internetSpeed,
          deskType: formData.deskType,
          isWheelchairAccessible: formData.isWheelchairAccessible,
          hasAdjustableHeight: formData.hasAdjustableHeight,
          hasSpecialLighting: formData.hasSpecialLighting,
          requiresSpecialAccommodation: formData.requiresSpecialAccommodation,
          accommodationType: formData.accommodationType,
          status: formData.status,
          isActive: formData.isActive,
          needsMaintenance: formData.needsMaintenance,
          underMaintenance: formData.underMaintenance,
          lastMaintenanceDate: formData.lastMaintenanceDate,
          nextMaintenanceDate: formData.nextMaintenanceDate,
          maintenanceNotes: formData.maintenanceNotes,
          // currentAssignmentType: formData.currentAssignmentType,
          // currentAssignmentUser: formData.currentAssignmentUser,
          // currentAssignmentStartDate: formData.currentAssignmentStartDate,
          // currentAssignmentEndDate: formData.currentAssignmentEndDate,
          isBookable: formData.isBookable,
          naturalLight: formData.naturalLight,
          noiseLevel: formData.noiseLevel,
          nearWindow: formData.nearWindow,
          ventilation: formData.ventilation,
          temperature: formData.temperature,
        };
        await addSeatMutation.mutateAsync({ spaceId: formData.coworking, data: newSeat });
      }
    } catch (error) {
      NotifyError(error.response.data.message, error);
    }
    finally {
      setModalOpen(false);
      setFormData(INITIAL_FORM_DATA);
      setIsUpdating(false);
      setSelectedSeatId(null);

    }
  };

  // Open modal for adding a new building
  const openAddSeatModal = () => {
    setModalOpen(true);
    const defaultValueForInitialFormData = { ...INITIAL_FORM_DATA };
    defaultValueForInitialFormData.status = 'operational';
    defaultValueForInitialFormData.isActive = true;
    defaultValueForInitialFormData.seatType = 'common';
    defaultValueForInitialFormData.deskType = 'fixed';
    defaultValueForInitialFormData.ventilation = 'ac';
    defaultValueForInitialFormData.temperature = '26';

    setFormData(defaultValueForInitialFormData);
    setIsUpdating(false);
  };

  // Open modal for updating a building
  const openUpdateSeatModal = (seat) => {
    setModalOpen(true);
    setFormData({ ...seat, coworking: seat.coworking?._id || '' });
    setIsUpdating(true);
    setSelectedSeatId(seat._id);
  };

  const interpretOccupency = (occupency) => {
    if (occupency === null) {
      return <Chip color="green" size="sm" className="text-xs" variant="ghost" value={`Full time`} />;
    }
    if (occupency.isFullyOccupied) {
      return <Chip color="red" size="sm" className="text-xs" variant="ghost" value={`Occupied`} />;
    }
    return <Chip color="blue" size="sm" className="text-xs" variant="ghost" value={`${occupency.occupiedHours} hours`} />;
  }

  const TABLE_COLUMNS = [
    {
      label: 'Seat #',
      key: 'seatCode',
      sortable: true,
      sort: sort === "seatCode" ? "-seatCode" : "seatCode"
    },
    {
      label: 'Space',
      key: 'space',
      sortable: false,
      sort: null
    },
    {
      label: 'Availability',
      key: 'availability',
      sortable: false,
      sort: null
    },
    {
      label: 'Curr Assign.',
      key: 'currentAssignment',
      sortable: false,
      sort: null
    },
    {
      label: 'Timestamp',
      key: 'createdDate',
      sortable: true,
      sort: sort === "createdDate" ? "-createdDate" : "createdDate"
    },
    {
      label: 'Actions',
      key: 'actions',
      sortable: false,
      sort: null,
      hidden: !(can('FX10-1', 'R') || can('FX10', 'edit'))
    }
  ].filter(col => !col.hidden);
  return (
    <div className={``}>
      <div className="m-5 p-5 border rounded-3xl">
        <PageHeader
          title="Seat Management"
          isLoading={isSeatsLoading}
          actionOnClick={can('FX10', 'edit') ? openAddSeatModal : undefined}
          actionType="add"
          actionText="Add New Seat"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mt-2">
          <Input
            label="Search Seats"
            placeholder="Search by seat code "
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={isTyping ? <Spinner className="h-4 w-4" /> : <XIcon className={searchTerm ? "cursor-pointer text-gray-500" : "hidden"} size={16} onClick={() => setSearchTerm('')} />}
          />
          <Select
            label="Filter by Seat Type"
            value={filterSeatType}
            onChange={(value) => setFilterSeatType(value)}
          >
            {
              ACCOMMODATION_TYPES_ENUM.map((type, index) => (
                <Option key={index} value={type}>{convertToPresentableText(type)}</Option>
              ))
            }
          </Select>
          <Select
            label="Filter by Status"
            value={filterStatus}
            onChange={(value) => setFilterStatus(value)}
          >
            {OPERATIONAL_STATUSES_ENUM.map((status, index) => (
              <Option key={index} value={status}>{convertToPresentableText(status)}</Option>
            ))}
          </Select>
        </div>
        <div className="overflow-x-auto">
          {
            isSeatsLoading ?
              <div className="flex justify-center">
                <Spinner />
              </div> :
              <div className="overflow-x-auto mt-4">
                {isSeatsError ?
                  <div className="flex justify-center">
                    <p className="text-red-500">Error loading seats</p>
                  </div> : (
                    seats?.length > 0 ?
                      (
                        <table className="w-full min-w-max table-auto text-left">
                          <thead className="text-sm">
                            <tr className="py-3">
                              <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Seats ({seats?.length})</th>
                            </tr>
                            <tr className="py-3 capitalize">
                              {
                                TABLE_COLUMNS.map((column, index) => (
                                  <th
                                    key={index}
                                    className={"cursor-pointer py-3"}
                                    onClick={() => setSort(column.sortable ? column.sort : sort)}
                                  >
                                    <Typography
                                      variant="medium"
                                      color="blue-gray"
                                      className="flex items-center gap-2 font-bold leading-none opacity-70">
                                      {column.label} {column.sortable ? (column.sort.startsWith('-') ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />) : ''}
                                    </Typography>
                                  </th>
                                ))
                              }
                            </tr>
                          </thead>
                          <tbody>
                            {seats?.map((seat, index) => (
                              <tr key={index} draggable className={`border-t hover:bg-gray-50 button rounded-full ${seat.status === OPERATIONAL_STATUSES.CLOSED ? 'text-gray-500' : ''}`}>
                                <td className="py-3 text-sm">
                                  <div className="text-xs font-bold">
                                    {seat.seatCode} ({seat.seatType})
                                  </div>
                                  <div className="text-xs">
                                    {InterpretOperationalStatus(seat.status, "text")}
                                  </div>
                                </td>
                                <td className="font-thin py-3 text-sm">
                                  <div className="font-bold text-sm">
                                    {seat.coworking?.code || 'N/A'}
                                  </div>
                                  <div className="text-xs text-gray-900">
                                    {seat.coworking?.floor?.floorCode} - {seat.coworking?.floor?.building?.name}
                                  </div>
                                </td>
                                <td className="font-thin py-3 text-sm">
                                  {
                                    seat.status === OPERATIONAL_STATUSES.OPERATIONAL ? (
                                      <div className="flex">
                                        {interpretOccupency(seat.occupency)}
                                      </div>
                                    ) : (
                                      <div className="flex">
                                        {InterpretOperationalStatus(seat.status)}
                                      </div>
                                    )
                                  }
                                </td>
                                <td className="font-thin py-3 text-sm">
                                  {
                                    seat.currentBookingUsers && seat.currentBookingUsers.length > 0 ? (
                                      <div className="flex gap-1">
                                        {seat.currentBookingUsers.map(user => (
                                          <div className="flex gap-1" key={user._id}>
                                            <Link className="text-xs text-blue-600 hover:text-orange-600 duration-300 flex items-center gap-1" to={`/customer/${user._id}`} target="_blank">
                                              <UserIcon size={12} /> {user.name}
                                              {/* <SquareArrowOutUpRightIcon size={12} /> */}
                                            </Link>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      seat.status === OPERATIONAL_STATUSES.OPERATIONAL ? (
                                        <div className="flex">
                                          <Chip color="green" size="sm" className="text-xs" variant="ghost" value="Available" />
                                        </div>
                                      ) : (
                                        <div className="flex">
                                          {InterpretOperationalStatus(seat.status)}
                                        </div>
                                      )
                                    )
                                  }
                                </td>
                                <td className="font-thin py-3 text-sm">
                                  <div className="flex flex-col">
                                    <div className="text-xs">C: {moment(seat.createdDate).format('lll')}</div>
                                    <div className="text-xs">U: {moment(seat.lastUpdated).format('lll')}</div>
                                  </div>
                                </td>
                                {(can('FX10-1', 'R') || can('FX10', 'edit')) && (
                                <td className="flex gap-1 py-3">
                                  {can('FX10-1', 'R') && (
                                  <Link to={`/seat/${seat._id}`}>
                                    <IconButton
                                      size="sm"
                                      color="blue"
                                      className="rounded-full"
                                    >
                                      <EyeIcon size={12} />
                                    </IconButton>
                                  </Link>
                                  )}
                                  {can('FX10', 'edit') && (
                                  <IconButton
                                    size="sm"
                                    color="orange"
                                    className="rounded-full"
                                    onClick={() => openUpdateSeatModal(seat)}
                                  >
                                    <Edit2Icon size={12} />
                                  </IconButton>
                                  )}
                                </td>
                                )}
                              </tr>
                            ))}
                          </tbody>

                        </table>
                      ) : (
                        <div className="flex justify-center">
                          <p className="text-red-500">No seats found</p>
                        </div>
                      )
                  )
                }
              </div>

          }
        </div>

        {/* Modal for Add/Update Seat */}
        <ModalX isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={isUpdating ? "Update Seat" : "Add New Seat"}
          maxWidth="xxl">
          <div className={`flex gap-5 flex-col md:flex-row`}>
            <div className={`w-full `}>
              <form onSubmit={handleFormSubmit}>
                <div className="space-y-2">
                  <div className="pb-3">
                    <Tabs value="general" >
                      <TabsHeader className="rounded-full" indicatorProps={{ className: 'rounded-full' }}>
                        {
                          addModalTabs.map((tab, index) => (
                            <Tab key={index} className="rounded-full font-thin text-xs" value={tab.value}>
                              <span className={`hidden md:block`}>{tab.label}</span>
                              <span className={`block md:hidden`}>{tab.icon}</span>
                            </Tab>
                          ))
                        }
                      </TabsHeader>
                      <TabsBody className="min-h-[500px]">
                        <TabPanel value={addModalTabs[0].value} className="space-y-4">
                          {
                            isSpacesError && <div className="text-center py-5 text-red-500">Error loading spaces. Please try again.</div>
                          }
                          <div className={`flex gap-4 flex-col md:flex-row`}>
                            <Input
                              label="Seat Code"
                              name="seatCode"
                              value={formData.seatCode}
                              onChange={handleInputChange}
                              required
                            />
                            {/* 'available', 'occupied', 'reserved', 'maintenance', 'blocked' */}
                            <Select
                              label="Status"
                              value={formData.status}
                              onChange={newValue => handleSelectChange("status", newValue)}
                            >
                              {
                                OPERATIONAL_STATUSES_ENUM.map((status, index) => (
                                  <Option key={index} value={status}>
                                    {convertToPresentableText(status)}
                                  </Option>
                                ))
                              }
                            </Select>
                          </div>
                          {
                            isSpacesLoading ? <Spinner /> :
                              isSpacesError ? <div className="text-center py-5 text-red-500">Error loading spaces. Please try again.</div> :
                                <Select
                                  label={'Select Space'}
                                  value={formData.coworking}
                                  onChange={(newValue) => handleSelectChange("coworking", newValue)}
                                >
                                  {
                                    spaces?.map((space, index) => (
                                      <Option key={index} value={space._id}>
                                        <div className="flex justify-between gap-3 w-full">
                                          <div className="">
                                            {getOrdinal(space.number)} - {space?.code || 'N/A'}
                                          </div>
                                          <div className="">
                                            {space.floor.building.name} - {space.floor.building.branch.name}
                                          </div>
                                        </div>
                                      </Option>
                                    ))
                                  }
                                </Select>
                          }

                          <div className={`flex gap-4 flex-col md:flex-row`}>
                            <Select
                              label="Seat Type"
                              value={formData.seatType}
                              onChange={newValue => handleSelectChange("seatType", newValue)}
                            >
                              {
                                ACCOMMODATION_TYPES_ENUM.map((type, index) => (
                                  <Option key={index} value={type}>
                                    {convertToPresentableText(type)}
                                  </Option>
                                ))
                              }
                            </Select>
                            <Select
                              label="Desk Type"
                              value={formData.deskType}
                              onChange={newValue => handleSelectChange("deskType", newValue)}
                            >
                              {
                                DESK_TYPES_ENUM.map((type, index) => (
                                  <Option key={index} value={type}>
                                    {convertToPresentableText(type)}
                                  </Option>
                                ))
                              }
                            </Select>

                          </div>
                          <div className={`flex flex-col`}>
                            <Checkbox
                              name="naturalLight"
                              checked={formData.naturalLight}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Has Natural Light"
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              name="nearWindow"
                              checked={formData.nearWindow}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Adjacent Window"
                              onChange={handleCheckBoxChange}
                            />

                            <Checkbox
                              name="dedicatedInternet"
                              checked={formData.dedicatedInternet}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Dedicated Internet"
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              name="dedicatedClimateControl"
                              checked={formData.dedicatedClimateControl}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Dedicated Climate Control"
                              onChange={handleCheckBoxChange}
                            />
                            <hr />
                            <div className={`flex flex-col md:flex-row md:items-center mt-4`}>
                              <p>Ventilation</p>
                              <div className="flex">
                                <Radio name="ventilation" value={formData.ventilation} label="Natural" onChange={() => handleSelectChange("ventilation", "natural")} />
                                <Radio name="ventilation" value={formData.ventilation} label="AC" defaultChecked onChange={() => handleSelectChange("ventilation", "ac")} />
                                <Radio name="ventilation" value={formData.ventilation} label="Both" onChange={() => handleSelectChange("ventilation", "both")} />
                              </div>
                            </div>
                          </div>
                        </TabPanel>
                        <TabPanel value={addModalTabs[1].value} className="space-y-4 flex flex-col md:flex-row gap-4">
                          {/* 
                              hasMonitorArm
                              hasKeyboard
                              hasMouse
                              hasErgonomicChair
                              hasFootRest
                              hasPrivacyScreen
                              hasUSBPorts
                              powerOutlets
                              hasWiredInternet
                              internetSpeed 
                              */}
                          <div className={`flex flex-col`}>
                            <Checkbox
                              name="hasMonitorArm"
                              checked={formData.hasMonitorArm}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Has Monitor Arm"
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              name="hasKeyboard"
                              checked={formData.hasKeyboard}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Dedicated Keyboard"
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              name="hasMouse"
                              checked={formData.hasMouse}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Dedicated Mouse"
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              name="hasErgonomicChair"
                              checked={formData.hasErgonomicChair}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Ergonomic Chair"
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              name="hasFootRest"
                              checked={formData.hasFootRest}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Foot Rest"
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              name="hasPrivacyScreen"
                              checked={formData.hasPrivacyScreen}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Privacy Partition"
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              name="hasWiredInternet"
                              checked={formData.hasWiredInternet}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Wired Internet"
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              name="hasUSBPorts"
                              checked={formData.hasUSBPorts}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              label="Has USB Ports"
                              onChange={handleCheckBoxChange}
                            />
                          </div>
                          <div className={`flex flex-col gap-4`}>
                            <Select
                              label="Power Outlets"
                              value={formData.powerOutlets}
                              onChange={newValue => handleSelectChange("powerOutlets", newValue)}
                            >
                              {[0, 1, 2, 3, 4, 5, 6].map((x, i) => (<Option value={x}>{x}</Option>))}
                            </Select>
                            <Input
                              label="Internet Speed"
                              name="internetSpeed"
                              value={formData.internetSpeed}
                              onChange={handleInputChange}
                            />
                            <Input
                              label="Average Temperature"
                              name="temperature"
                              value={formData.temperature}
                              onChange={handleInputChange}
                            />
                          </div>
                        </TabPanel>
                        <TabPanel value={addModalTabs[2].value} className="space-y-4 flex flex-col gap-4">
                          <Alert variant="outlined">
                            <span className="font-thin text-xs">
                              This Functionality will be available soon.
                            </span>
                          </Alert>
                          <div className={`flex justify-between items-center`}>
                            <div className={``}>
                              <span className="font-normal text-md">Provided Equipment List</span>
                              <p className="font-thin text-xs">Add the items provided to this seat</p>
                            </div>
                            <Button
                              // onClick={handleAddEquipment} 
                              className="rounded-full"
                              size="sm"
                              disabled
                            >Add</Button>
                          </div>
                          {
                            [1].map((eq, i) => (
                              <div key={i} className="flex gap-4">
                                <Input
                                  label={`Item / Model`}
                                  name={`eq_${i + 1}`}
                                  value={eq}
                                  onChange={e => handleInputChange(`eq_${i + 1}`, e.target.value)}
                                  disabled
                                />
                                <Input
                                  label="Serial #"
                                  name={`eq_type_${i + 1}`}
                                  value={formData[`eq_type_${i + 1}`]}
                                  onChange={e => handleInputChange(`eq_type_${i + 1}`, e.target.value)}
                                  disabled
                                />
                                <Input
                                  label="Last Maintainence"
                                  name={`eq_type_${i + 1}`}
                                  type="date"
                                  value={formData[`eq_type_${i + 1}`]}
                                  onChange={e => handleInputChange(`eq_type_${i + 1}`, e.target.value)}
                                  disabled
                                />
                              </div>
                            ))
                          }
                        </TabPanel>
                        <TabPanel value={addModalTabs[3].value} className="">
                          {/* 
                            isWheelchairAccessible
                            hasAdjustableHeight
                            hasSpecialLighting
                            requiresSpecialAccommodation
                            accommodationType
                          */}
                          <div className={`flex flex-col`}>
                            <Checkbox
                              label="Seat Accessible For Wheel Chair"
                              name="isWheelchairAccessible"
                              checked={formData.isWheelchairAccessible}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              label="Provided Seat has Adjustable Height"
                              name="hasAdjustableHeight"
                              checked={formData.hasAdjustableHeight}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              onChange={handleCheckBoxChange}
                            />

                            <Checkbox
                              label="Has Special Dedicated Lighting"
                              name="hasSpecialLighting"
                              checked={formData.hasSpecialLighting}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              onChange={handleCheckBoxChange}
                            />
                            <Checkbox
                              label="Special Accomodation"
                              name="requiresSpecialAccommodation"
                              checked={formData.requiresSpecialAccommodation}
                              className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                              onChange={handleCheckBoxChange}
                            />
                          </div>
                        </TabPanel>
                        {/* <TabPanel value={addModalTabs[4].value} className="">
                          <Alert variant="outlined">
                            <span className="font-thin text-xs">
                              This Functionality will be available soon.
                            </span>
                          </Alert>
                        </TabPanel> */}
                      </TabsBody>
                    </Tabs>
                  </div>


                  {/* <div className="">
                <span className="text-md font-thin text-gray-900">
                  Location
                </span>
              </div> */}
                  {/* <div className="flex gap-4">
                <Input
                  label="Google Maps Link"
                  name="googleMapsLink"
                  value={formData.googleMapsLink}
                  onChange={handleInputChange}
                  icon={formData.googleMapsLink !== "" ? (googleMapsLinkStatus ? <Check size={12} /> : <ShieldAlert size={12} />) : <></>}
                />

                <Input
                  label="Longitude"
                  name="lng"
                  value={formData.lng}
                  onChange={handleInputChange}
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
                <Input
                  label="Latitude"
                  name="lat"
                  value={formData.lat}
                  onChange={handleInputChange}
                  type="number"
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
              </div> */}
                </div>
                <hr />
                <div className="mt-4 flex justify-end space-x-2">
                  <Button
                    onClick={() => setModalOpen(false)}
                    className="rounded-full "
                    variant="text"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="rounded-full">
                    {isUpdating ? "Update" : "Add"}
                  </Button>
                </div>
              </form>
            </div>
            <div className={`w-full hidden border rounded-3xl p-4`}>
              summary
            </div>
          </div>
        </ModalX>
      </div>
      <ConfirmationDialog />
    </div>
  );
};

export default SeatPage;
